// Экран «Ресурсы» — тринадцатый экран нового фронта (п.20, 2026-09-07).
// Назначение ресурсов работам проекта: ресурс из списка, количество человек
// и выработка на человека в день. Правка по клику в ячейке; действуют фильтры оболочки.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { call, p, type Data } from '../api';
import type { ShellCtx } from '../shell';

type Work = Data<'/api/projects/{pid}/works', 'get'>[number];
type Spisok = Data<'/api/projects/{pid}/spiski', 'get'>[number];

const num = (v: string) => (v.trim() ? +v.replace(',', '.') : null);

export function Resurs({ ctx }: { ctx: ShellCtx }) {
  const [works, setWorks] = useState<Work[]>([]);
  const [resursy, setResursy] = useState<string[]>([]);
  const can = ctx.canPlan;

  const load = useCallback(async () => {
    const [w, sp] = await Promise.all([
      call<Work[]>('get', p('/api/projects/{pid}/works', { pid: ctx.pid })),
      call<Spisok[]>('get', p('/api/projects/{pid}/spiski', { pid: ctx.pid })),
    ]);
    if (!w.ok) { ctx.onError?.(w.error.message); return; }
    setWorks(w.data);
    if (sp.ok) setResursy(sp.data.filter(x => x.kod === 'resurs').map(x => x.znachenie).filter(Boolean));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.pid, ctx.reloadToken]);
  useEffect(() => { void load(); }, [load]);

  const rows = useMemo(
    () => works.filter(w => !ctx.matchesWork || ctx.matchesWork(w.name)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [works, ctx.filterKey]);

  async function patch(id: number, body: Record<string, unknown>) {
    const r = await call('patch', p('/api/works/{wid}', { wid: id }), body);
    if (!r.ok) { ctx.onError?.(r.error.message); void load(); return; }
    ctx.onError?.('');
    ctx.onSaved?.();
    void load();
  }

  return (
    <div>
      <div className="tbl" style={{ maxHeight: '76vh' }}>
        <table>
          <thead>
            <tr>
              <th>Работа</th><th>Ресурс</th>
              <th title="Количество человек в ресурсе">Чел.</th>
              <th title="Выработка на человека в день">Выработка</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(w => (
              <tr key={w.id}>
                <td>{w.name}</td>
                <td className="num">
                  {can ? (
                    <select value={w.resurs || ''} onChange={e => void patch(w.id, { resurs_imya: e.target.value || null })}>
                      <option value="" />
                      {resursy.map(v => <option key={v}>{v}</option>)}
                      {w.resurs && !resursy.includes(w.resurs) && <option>{w.resurs}</option>}
                    </select>
                  ) : (w.resurs || '')}
                </td>
                <NumCell value={w.chel} can={can} onSave={v => patch(w.id, { resurs_kolvo: num(v) })} />
                <NumCell value={w.trud} can={can} onSave={v => patch(w.id, { trudoemkost: num(v) })} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="muted" style={{ marginTop: 8 }}>
        Назначение ресурсов работам проекта: клик по ячейке — правка (ресурс — из списка,
        названия ведутся в «Списках»). Работы добавляются на вкладке «Работы»; действуют фильтры сверху.
      </div>
    </div>
  );
}

/** числовая ячейка с правкой по клику */
function NumCell({ value, can, onSave }: { value?: number | null; can: boolean; onSave: (v: string) => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value != null ? String(value) : '');
  useEffect(() => { setV(value != null ? String(value) : ''); }, [value]);
  if (!editing) {
    return <td className={'num' + (can ? ' edit' : '')} onClick={() => { if (can) setEditing(true); }}>
      {value ?? ''}
    </td>;
  }
  const done = async (save: boolean) => {
    setEditing(false);
    const was = value != null ? String(value) : '';
    if (save && v !== was) await onSave(v); else setV(was);
  };
  return (
    <td className="num">
      <input autoFocus value={v} style={{ width: '100%' }} onChange={e => setV(e.target.value)}
        onBlur={() => void done(true)}
        onKeyDown={e => { if (e.key === 'Enter') void done(true); if (e.key === 'Escape') void done(false); }} />
    </td>
  );
}
